import { useEffect } from 'react';
import { Proposal } from 'types/snapshot';
import { RiShareLine } from 'react-icons/ri';
import { stateToColor } from 'helpers/snapshot';
import { FaTwitter, FaRegCopy } from 'react-icons/fa';
import {
  Skeleton,
  Heading,
  HStack,
  Tag,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Text,
  useClipboard,
  SkeletonText,
  useToast
} from '@chakra-ui/react';

import ReactMarkdown from 'react-markdown';
import ChakraUIRenderer from 'chakra-ui-markdown-renderer';
import remarkGfm from 'remark-gfm';

type Props = {
  proposal?: Proposal;
};

const ProposalSummary: React.FC<Props> = ({ proposal }) => {
  const { hasCopied, onCopy } = useClipboard(window.location.href);
  const toast = useToast();

  useEffect(() => {
    if (hasCopied) {
      toast({
        title: 'Copied link',
        status: 'info',
        duration: 4000,
        isClosable: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCopied]);

  return (
    <>
      <Skeleton isLoaded={!!proposal} my={5}>
        <Heading size='lg'>{proposal?.title}</Heading>
      </Skeleton>
      <Skeleton isLoaded={!!proposal} mb={3}>
        <HStack justifyContent='space-between'>
          <Tag variant='solid' colorScheme={stateToColor(proposal?.state!)}>
            {proposal?.state}
          </Tag>
          <Text>
            <Menu autoSelect={false} placement='bottom-end'>
              <MenuButton
                as={Button}
                leftIcon={<RiShareLine />}
                variant='ghost'
              >
                Share
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<FaTwitter />}
                  onClick={() =>
                    window.open(
                      `https://twitter.com/intent/tweet?text=${window.location.href}`,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  Twitter
                </MenuItem>
                <MenuItem icon={<FaRegCopy />} onClick={onCopy}>
                  Copy Link
                </MenuItem>
              </MenuList>
            </Menu>
          </Text>
        </HStack>
      </Skeleton>
      <SkeletonText
        isLoaded={!!proposal}
        noOfLines={3}
        spacing='12px'
        mb={2}
        fontSize='lg'
        lineHeight='1.5'
        whiteSpace='pre-wrap'
        fontWeight='500'
        color='gray.200'
      >
        <ReactMarkdown
          components={ChakraUIRenderer()}
          remarkPlugins={[remarkGfm]}
        >
          {proposal?.body || ''}
        </ReactMarkdown>
      </SkeletonText>
    </>
  );
};

export default ProposalSummary;
